import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { AuthService } from '../../services/auth.service';

export function passwordPatternValidator(): ValidationErrors | null {
  return (control: AbstractControl): ValidationErrors | null => {
    const value: string = control.value || '';
    const pattern =
      /^(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#.])[A-Za-z\d@$!%*?&#.]{8,}$/;

    return pattern.test(value) ? null : { passwordPattern: true };
  };
}

export function passwordMatchValidator(
  controlName: string,
  matchingControlName: string
) {
  return (formGroup: AbstractControl): ValidationErrors | null => {
    const control = formGroup.get(controlName);
    const matchingControl = formGroup.get(matchingControlName);

    if (!control || !matchingControl) {
      return null;
    }

    if (matchingControl.errors && !matchingControl.errors['passwordMismatch']) {
      return null;
    }

    if (control.value !== matchingControl.value) {
      matchingControl.setErrors({ passwordMismatch: true });
      return { passwordMismatch: true };
    } else {
      matchingControl.setErrors(null);
      return null;
    }
  };
}

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, RouterModule],
  templateUrl: './reset-password.component.html',
  styleUrls: ['./reset-password.component.css'],
})
export class ResetPasswordComponent implements OnInit {
  resetPasswordForm!: FormGroup;
  isSubmitting = false;
  errorMessage = '';
  successMessage = '';
  token = '';
  showPassword = false;
  showConfirmPassword = false;
  tokenInvalid = false;
  resetSuccess = false;
  redirectProgress = 0;
  redirectTimer: any = null;
  redirectDuration = 3;

  constructor(
    private formBuilder: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    this.resetPasswordForm = this.formBuilder.group(
      {
        password: [
          '',
          [
            Validators.required,
            Validators.minLength(8),
            passwordPatternValidator(),
          ],
        ],
        confirmPassword: ['', Validators.required],
      },
      {
        validators: passwordMatchValidator('password', 'confirmPassword'),
      }
    );

    this.route.queryParams.subscribe((params) => {
      this.token = params['token'];
      if (!this.token) {
        this.tokenInvalid = true;
        this.errorMessage = 'Password reset token is missing or invalid';
      }
    });

    setTimeout(() => {
      document.querySelector('.reset-password-form')?.classList.add('visible');
    }, 100);
  }

  get password() {
    return this.resetPasswordForm.get('password');
  }

  get confirmPassword() {
    return this.resetPasswordForm.get('confirmPassword');
  }

  togglePasswordVisibility(): void {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  onSubmit(): void {
    if (this.resetPasswordForm.invalid || this.tokenInvalid) {
      Object.keys(this.resetPasswordForm.controls).forEach((key) => {
        const control = this.resetPasswordForm.get(key);
        control?.markAsTouched();
      });
      return;
    }

    this.isSubmitting = true;
    this.errorMessage = '';
    this.successMessage = '';

    this.authService.resetPassword(this.token, this.password?.value).subscribe({
      next: (response) => {
        this.isSubmitting = false;
        this.successMessage =
          response.message || 'Password has been reset successfully.';
        this.resetSuccess = true;

        this.startRedirectCountdown();
      },
      error: (error) => {
        this.isSubmitting = false;
        this.errorMessage =
          error.message || 'Failed to reset password. Please try again.';
      },
    });
  }

  startRedirectCountdown(): void {
    const startTime = Date.now();
    const duration = this.redirectDuration * 1000;

    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
    }

    this.redirectTimer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      this.redirectProgress = Math.min((elapsed / duration) * 100, 100);

      if (elapsed >= duration) {
        clearInterval(this.redirectTimer);
        this.router.navigate(['/login']);
      }
    }, 50);
  }

  ngOnDestroy(): void {
    if (this.redirectTimer) {
      clearInterval(this.redirectTimer);
    }
  }
}
